import { createContext, useContext } from 'react';
import { AdMeshSDK } from '../sdk/AdMeshSDK';
import type { AdMeshTheme, PlatformResponse, ActiveDelegationSession } from '../types/index';
import type { Suggestion } from '../components/AdMeshIntentAssistant';

/**
 * Context value provided by AdMeshProvider
 */
export interface AdMeshContextValue {
  // SDK instance
  sdk: AdMeshSDK | null;

  // Configuration
  apiKey: string;
  sessionId: string;
  theme?: AdMeshTheme;

  // Canonical AIP PlatformRequest inputs (optional, passed from frontend)
  language?: string; // User language in BCP 47 format (e.g., "en-US")
  location?: {
    city?: string;
    state?: string;
    stateCode?: string;
    country?: string;
    countryCode?: string;
    zipcode?: string;
    latitude?: number;
    longitude?: number;
  };
  userId?: string; // Anonymous hashed user ID
  model?: string; // AI model identifier (e.g., "gpt-4o")
  messages?: Array<{ role: string; content: string; id?: string }>; // Conversation history

  // Tracking state
  processedMessageIds: Set<string>;
  recommendationResults: Map<string, PlatformResponse | null>;
  activeDelegationSession: ActiveDelegationSession | null;

  // Suggestion state
  suggestions: Suggestion[];
  isAnalyzingIntent: boolean;
  intentError: string | null;

  // Methods
  markMessageAsProcessed: (messageId: string) => void;
  isMessageProcessed: (messageId: string) => boolean;
  setRecommendationResult: (messageId: string, result: PlatformResponse | null) => void;
  getRecommendationResult: (messageId: string) => PlatformResponse | null | undefined;
  hasRecommendationResult: (messageId: string) => boolean;
  setActiveDelegationSession: (session: ActiveDelegationSession | null) => void;
  getActiveDelegationSession: () => ActiveDelegationSession | null;
  hasActiveDelegationSession: () => boolean;
  refreshDelegationActivity: (eventType?: string, metadata?: Record<string, unknown>) => Promise<void>;
  stopDelegationSession: (reason?: string, metadata?: Record<string, unknown>) => Promise<void>;
  retryIntentAnalysis: () => Promise<void>;
}

/**
 * React Context for AdMesh SDK
 * 
 * Provides SDK instance and tracking state to all child components
 */
export const AdMeshContext = createContext<AdMeshContextValue | undefined>(
  undefined
);

/**
 * Hook to access AdMesh context
 * 
 * @throws Error if used outside of AdMeshProvider
 * @returns AdMeshContextValue
 */
export function useAdMeshContext(): AdMeshContextValue {
  const context = useContext(AdMeshContext);
  
  if (!context) {
    throw new Error(
      'useAdMeshContext must be used within an <AdMeshProvider>. ' +
      'Make sure your component is wrapped with <AdMeshProvider>.'
    );
  }
  
  return context;
}
